import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of, combineLatest, forkJoin } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { HTTPCONSTANTS } from '../../../http/http-constants';
import {
  NewReleasePageResponse,
  NewReleaseFirstSold,
  FirstSoldEntry,
  NewReleaseHeadingToAuction,
  HeadingToAuctionEntry,
  PerfectPlatesSection,
  PerfectSectionId,
  PlateCard,
  PlateState,
  PlateHistory,
  DvlaPreSale,
  PreReleaseAsset,
  PreReleaseLists,
  TickerEntry,
} from '../models/new-release-page.types';
import { buildScaffold, isCurated, isPreRelease, PERFECT_SECTION_META } from '../data/new-release-fixture';
import { NewReleaseResult } from '../../../models/newReleaseResult';
import { RegistrationService } from '../../../services/registration-service';
import { SessionService } from '../../../services/session-service';

// Source-of-truth contract for /new-release/:year. The page is composed
// from real endpoints only; no fake plates or prices ever survive into
// the response object. Where a section has no real source yet we surface
// an empty list / null featured, and the template renders an empty state.
//
//   - Ticker:    GET /api/MarketWatch/recently-sold-by-series?series={year}
//                — real soldplate rows for the series since launch date,
//                ordered freshest-first. See PlatexAPI MarketWatchController
//                GetRecentlySoldBySeries for the query + sale_type filter.
//                Returns 200 with [] when a series has no rows in the
//                window; the hero's *ngIf="ticker?.entries?.length" hides
//                the rail in that case. No fallback / picks / featured
//                path — empty stays empty.
//   - Perfect:   ?process=new-release&filters=new-{year}-release
//                (same endpoint the legacy /new-release/26 page reads).
//   - Names:     ?process=new-release-name&filters={year}.
//   - Banned:    real, from the FOI file at data/banned-76.ts (series 76 only;
//                empty for every other year until the FOI file ships).
//   - Series:    derived from the URL year param + a per-year scaffold; copy
//                fields (releasedDate, combinationsCount, vehiclesFromDate,
//                v750Retention, headlineCopy, subCopy) are left empty when we
//                have no verified source. The template renders placeholders /
//                hides rows rather than leak a guess.
//
// Perfect Plates is returned as a flat list; the wordbank.category column
// (handoff §5.1) hasn't been backfilled, so we classify client-side by
// term shape — zero spaces / 7 chars → single_word; one space (3,4) → 3+4;
// (4,3) → 4+3; (2,5) → 2+5; anything else dropped.

/**
 * Shape of a row returned by /api/MarketWatch/recently-sold-by-series.
 * Camel-case JSON via Newtonsoft `[JsonProperty]` on the C# side; matches
 * the RecentlySoldPlate TS class used by the homepage live-feed chart.
 */
interface RecentlySoldRow {
  registration: string;
  soldPrice: number;
  soldDate: string;
}

/** Max ticker entries (rendered marquee-style; duplicated for seamless loop). */
const TICKER_MAX_ENTRIES = 30;

/** A "full" first-sold set. Below this the launch-rush duration is suppressed. */
const FIRST_SOLD_FULL_SET = 100;

export type PagePhaseResult =
  | { status: 'ok'; data: NewReleasePageResponse }
  | { status: 'not-yet-available'; year: string };

/** @deprecated retained for callers; prefer PagePhaseResult. */
export type FixturePhaseResult = PagePhaseResult;

@Injectable({ providedIn: 'root' })
export class NewReleasePageService {
  constructor(
    private http: HttpClient,
    private registrationService: RegistrationService,
    private sessionService: SessionService,
  ) {}

  getPage(year: string): Observable<PagePhaseResult> {
    if (isPreRelease(year) || isCurated(year)) return this.getPreReleasePage(year);
    const perfect$ = this.fetchPerfect(year);
    const names$ = this.fetchNames(year);
    const ticker$ = this.getRecentlySoldForYear(year).pipe(
      catchError(() => of([] as TickerEntry[])),
    );
    const firstSold$ = this.getFirstSoldForYear(year).pipe(
      catchError(() => of({ entries: [], rushDuration: null } as NewReleaseFirstSold)),
    );

    return combineLatest([perfect$, names$, ticker$, firstSold$]).pipe(
      map(([perfect, names, ticker, firstSold]) => {
        const scaffold = buildScaffold(year);
        const perfectClassified = this.classifyPerfectPlates(perfect);
        const namesBuilt = this.buildNamesSection(names);
        const headingToAuction = this.buildHeadingToAuction(perfect, names);
        // Ticker shows real sold plates for this year ONLY — never featured
        // listings labelled as sold. Hugh's rule: if no real 76 sales exist
        // yet, the rail hides (hero's *ngIf="entries?.length"). Source is the
        // sold-plate placement endpoint (curated, hourly populated); when it
        // catches up the rail auto-populates with real sales.
        const tickerEntries = ticker || [];
        console.info('[new-release] getPage', year, {
          perfectIn: perfect.length,
          perfectClassified: perfectClassified.totalCount,
          perfectSections: perfectClassified.sections.length,
          namesIn: names.length,
          namesOut: namesBuilt.totalCount,
          tickerOut: tickerEntries.length,
        });
        return {
          status: 'ok' as const,
          data: {
            ...scaffold,
            perfectPlates: perfectClassified,
            names: namesBuilt,
            ticker: {
              mode: 'recently_sold' as const,
              // Hugh's wording: "SOLD 76 REG" (rendered uppercase via the
              // ticker-label `text-transform: uppercase` rule).
              label: `Sold ${year} reg`,
              entries: tickerEntries,
            },
            headingToAuction: headingToAuction,
            firstSold: firstSold,
          },
        };
      }),
      catchError((err) => {
        // Pipeline-level safety net: if anything inside the map throws (e.g.
        // a malformed row breaks toPlateCard), the subscriber would otherwise
        // get no value and the page would stay in loading. Surface the error
        // and emit a minimal scaffold so banned + meta still render.
        console.error('[new-release] getPage pipeline crashed', err);
        return of({ status: 'ok' as const, data: buildScaffold(year) });
      }),
    );
  }

  /**
   * Pre-release page (series configured `preRelease` in the fixture, e.g. 27
   * before DVLA's November sale). Nothing is on sale, so the live endpoints
   * have nothing to say: the plates come from the static asset
   * assets/data/new-release/{year}.json — combinations that can be made
   * (valid memory tags, DVLA letter rules), every card priced TBC. The
   * auction and banned sections are labelled predictions, built from the
   * 26/75/76 FOI lists and every held-back plate since 2002; they get
   * scored against the real release (platex-valuation-lab
   * analysis/new_release_27/score_release.py).
   */
  private getPreReleasePage(year: string): Observable<PagePhaseResult> {
    const url = `/assets/data/new-release/${encodeURIComponent(year)}.json`;
    return this.http.get<PreReleaseAsset>(url).pipe(
      // Gated lists: the public asset carries counts only. Signed in, the
      // plates come from the API ([Authorize]); signed out (or on any API
      // error) the page renders the open half and the locked teaser.
      switchMap((asset) => this.withGatedLists(year, asset)),
      switchMap((asset) => (asset.released
        ? forkJoin([
            // Auction results too: many sold plates are back on the market with a dealer.
            this.fetchLive([...(asset.perfect || []), ...(asset.names || []), ...(asset.auction?.sold || [])].map((r) => r.plate)),
            this.getFirstSoldForYear(year),
          ]).pipe(map(([live, firstSold]) => ({ asset, live, firstSold: this.timedFirstSold(firstSold) })))
        : of({ asset, live: null as Map<string, NewReleaseResult> | null, firstSold: null as NewReleaseFirstSold | null }))),
      map(({ asset, live, firstSold }) => {
        const scaffold = buildScaffold(year, asset.meta as any);
        if (asset.dvlaChecked) scaffold.series.dvlaChecked = asset.dvlaChecked;
        if (asset.lists?.gated) {
          scaffold.series.listsServerGated = true;
          scaffold.series.listsTotal = asset.lists.total;
          scaffold.series.listsPerfect = asset.lists.perfect;
          scaffold.series.listsNames = asset.lists.names;
          if (asset.lists.unlocksAt) scaffold.series.listsUnlockAt = asset.lists.unlocksAt;
          scaffold.series.listsLocked = !!(asset.lists as { locked?: boolean }).locked;
        }
        const SECTION_BY_KIND: Record<string, PerfectSectionId> = { pattern: 'repeating', perfect_name: 'single_word' };
        const nameRegs = new Set((asset.perfect || []).filter((r) => r.kind === 'perfect_name').map((r) => r.plate));
        const SECTION_BY_GROUP: Record<string, PerfectSectionId> = {
          words: 'single_word', names: 'standout_names', foreign: 'standout_foreign', vehicles: 'standout_vehicles', almost: 'almost_perfect' };
        // Released series: live price/owner from plato-search; pre-release: TBC sentinel.
        const liveOf = (p: string) => live?.get(p);
        const toRow = (r: { plate: string; term: string; q: number; kind?: string; group?: string; languages?: string[] }): NewReleaseResult =>
          ({ ...(liveOf(r.plate) || {}), registration: r.plate, term: r.term,
             price: liveOf(r.plate)?.price ?? -1, owner: liveOf(r.plate)?.owner ?? -2, phrase_quality_score: r.q,
             languages: r.languages,
             nr_section: (r.group && SECTION_BY_GROUP[r.group]) || (r.kind ? SECTION_BY_KIND[r.kind] : undefined),
           } as unknown as NewReleaseResult);
        const perfect = this.classifyPerfectPlates((asset.perfect || []).map(toRow));
        // Almost perfect's example plate is the series' own (NO27 HRN = NORTHERN for 27).
        const almostCopy = (asset.meta as { almostCopy?: string } | undefined)?.almostCopy;
        const almostSec = perfect.sections.find((sec) => sec.id === 'almost_perfect');
        if (almostCopy && almostSec) almostSec.description = almostCopy;
        const watch = new Map<string, 'auction' | 'auction_only'>();
        [...(asset.perfect || []), ...(asset.names || [])]
          .forEach((r) => { if (r.watch) watch.set(r.plate, r.watch); });
        const hist = new Map<string, PlateHistory>();
        [...(asset.perfect || []), ...(asset.names || [])]
          .forEach((r) => { if (r.history) hist.set(r.plate, r.history); });
        const dvla = new Map<string, DvlaPreSale>();
        [...(asset.perfect || []), ...(asset.names || [])]
          .forEach((r) => { if (r.dvla) dvla.set(r.plate, r.dvla); });
        perfect.sections.forEach((sec) => sec.cards.forEach((c) => {
          const reg = c.plate.replace(/\s+/g, '');
          c.watch = watch.get(reg);
          if (nameRegs.has(reg)) c.isName = true;
          this.applyHistory(c, hist.get(reg));
          this.applyDvla(c, dvla.get(reg), scaffold.series.releasedDate);
        }));
        const picks = scaffold.series.ourPicks || [];
        if (picks.length) {
          const all = new Map<string, PlateCard>();
          perfect.sections.forEach((sec) => sec.cards.forEach((c) => all.set(c.plate.replace(/\s+/g, ''), c)));
          const cards = picks.map((p) => all.get(p)).filter((c): c is PlateCard => !!c)
            .map((c, i) => ({ ...c, qualityScore: picks.length - i }));
          if (cards.length) {
            perfect.sections.unshift({ id: 'our_picks', title: PERFECT_SECTION_META.our_picks.title,
              description: PERFECT_SECTION_META.our_picks.description, totalCount: cards.length,
              featured: cards[0], cards });
          }
        }
        const names = this.buildNamesSection((asset.names || []).map(toRow));
        names.cards.forEach((c) => {
          c.watch = watch.get(c.plate.replace(/\s+/g, ''));
          this.applyHistory(c, hist.get(c.plate.replace(/\s+/g, '')));
          this.applyDvla(c, dvla.get(c.plate.replace(/\s+/g, '')), scaffold.series.releasedDate);
        });
        const fmt = (p: string) => this.registrationService.LocalFormatRegistration(p.replace(/\s+/g, ''));
        const shownRegs = new Set([...(asset.perfect || []), ...(asset.names || [])].map((r) => r.plate));
        const a = asset.auction || { likely: [], watchWords: [], evidence: '' };
        const b = asset.banned || { likely: [], possible: [], patterns: [], evidence: '' };
        if (asset.released) {
          // Plates DVLA still holds for a future auction: tagged where they appear above,
          // listed after the auction results below.
          const heldNote = 'DVLA is holding this plate back for a future auction.';
          const heldRegs = new Set((a.held || []).map((w) => w.plate.replace(/\s+/g, '')));
          // The asset's held list is a build-time snapshot; the live feed's DVLA flag is current.
          live?.forEach((r, reg) => { if (r.auction_state === 'auction_reserved') heldRegs.add(reg); });
          const markHeld = (c: PlateCard) => {
            if (!heldRegs.has(c.plate.replace(/\s+/g, '')) || c.state === 'available' || c.state === 'resale') return;
            c.state = 'auction';
            c.price = null;
            c.historyNote = heldNote;
          };
          perfect.sections.forEach((sec) => sec.cards.forEach(markHeld));
          names.cards.forEach(markHeld);
          const held = (a.held || []).filter((w) => !shownRegs.has(w.plate.replace(/\s+/g, ''))).map((w) => {
            const card = this.toPlateCard(toRow({ plate: w.plate, term: w.term, q: 0 }));
            markHeld(card);
            return { plate: fmt(w.plate), word: w.term, phraseQualityScore: 0, soldPrice: null as number | null, soldDate: null as string | null, card };
          });
          return {
            status: 'ok' as const,
            data: {
              ...scaffold,
              perfectPlates: perfect,
              names,
              headingToAuction: {
                results: true,
                heldCount: (a.held || []).length,
                soldCount: (a.sold || []).length,
                ...(a.soldTotal ? { soldTotal: { count: a.soldTotal.count, pounds: Math.round(a.soldTotal.pence / 100),
                                                 since: a.soldTotal.since } } : {}),
                evidence: a.evidence,
                marqueePicks: [],
                // Plates already shown in a section above (with their result on the card) aren't repeated.
                // Each one is checked against the live market like the cards above: back on
                // sale = bright with today's price, otherwise greyed with the auction result.
                // Plates you can buy again come first.
                namesAuction: (a.sold || []).filter((w) => !shownRegs.has(w.plate.replace(/\s+/g, ''))).map((w) => {
                  const card = this.toPlateCard(toRow({ plate: w.plate, term: w.term, q: 0 }));
                  if (w.soldPence && w.date) this.applyHistory(card, { sold: { by: 'DVLA auction', pence: w.soldPence, date: w.date } });
                  return { plate: fmt(w.plate), word: w.term, phraseQualityScore: 0,
                    soldPrice: w.soldPence ? Math.round(w.soldPence / 100) : null, soldDate: w.date, card };
                }).sort((x, y) => Number(y.card.state === 'available' || y.card.state === 'resale')
                  - Number(x.card.state === 'available' || x.card.state === 'resale')).concat(held),
              },
              ...(firstSold ? { firstSold } : {}),
              // A hand-curated banned list in the scaffold (76: FOI list with groups) wins;
              // otherwise the asset's FOI list, or — where no FOI list was published —
              // plates inferred from the other series' lists, labelled as such.
              banned: scaffold.banned.totalCount ? scaffold.banned : {
                predicted: !!b.inferred,
                totalCount: (b.plates || []).length,
                intro: b.inferred
                  ? `No full DVLA list has been published for the ${year} series. These combinations were banned in other series' DVLA lists and never appeared on sale in ${year}, so they were very likely withheld.`
                  : `The DVLA withholds combinations considered offensive or unsuitable for display. This is the ${year} list from a Freedom of Information request to the DVLA.`,
                source: b.source,
                plates: (b.plates || []).map(fmt),
                wildcards: [],
                groups: [],
              },
            },
          };
        }
        return {
          status: 'ok' as const,
          data: {
            ...scaffold,
            perfectPlates: perfect,
            names,
            headingToAuction: {
              predicted: !a.confirmed,
              confirmed: !!a.confirmed,
              evidence: a.evidence,
              marqueePicks: (a.watchWords || []).map((w) => ({
                plate: fmt(w.plate), word: w.term, phraseQualityScore: w.q,
              })),
              // Best known first: readings DVLA has held back in the most earlier series (CH27 LOE, 49).
              namesAuction: (a.likely || []).filter((w) => !shownRegs.has(w.plate.replace(/\s+/g, '')))
                .slice().sort((x, y) => (y.priorSeries || 0) - (x.priorSeries || 0)).map((w) => ({
                  plate: fmt(w.plate), word: w.term, phraseQualityScore: 0,
                })),
            },
            banned: b.confirmed ? {
              predicted: false,
              totalCount: (b.plates || []).length,
              intro: b.evidence,
              source: b.source,
              plates: (b.plates || []).map(fmt),
              wildcards: [],
              groups: [],
            } : {
              predicted: true,
              totalCount: (b.likely || []).length,
              intro: `DVLA publishes the ${year} banned list after release. Until then this is our prediction: ` +
                `${b.evidence} The likely tier was banned in two or more of the 26, 75 and 76 series.`,
              source: 'PlateX prediction from DVLA FOI lists for 26, 75 and 76',
              plates: (b.likely || []).map(fmt),
              possiblePlates: (b.possible || []).map(fmt),
              wildcards: b.patterns || [],
              groups: [],
            },
          },
        };
      }),
      catchError((err) => {
        console.error('[new-release] pre-release asset failed', { url, year, err });
        return of({ status: 'ok' as const, data: buildScaffold(year) });
      }),
    );
  }

  /** Merge the signed-in lists into a gated pre-release asset. */
  private withGatedLists(year: string, asset: PreReleaseAsset): Observable<PreReleaseAsset> {
    if (!asset.lists?.gated || asset.perfect?.length || !this.sessionService.isLoggedIn()) return of(asset);
    const url = `${HTTPCONSTANTS.protocol}${HTTPCONSTANTS.apiBaseAddress}/NewRelease/prerelease/${encodeURIComponent(year)}/lists`;
    return this.http.get<PreReleaseLists>(url).pipe(
      map((lists) => (lists?.locked
        ? { ...asset, lists: { ...asset.lists!, unlocksAt: lists.unlocksAt || asset.lists!.unlocksAt, locked: true } as any }
        : { ...asset, perfect: lists?.perfect || [], names: lists?.names || [] })),
      catchError((err) => {
        console.error('[new-release] gated lists failed', { year, status: err?.status });
        return of(asset);
      }),
    );
  }

  /** Live price / owner for a curated list (plato-search `filters=list:`), in
   *  URL-sized chunks. A plato-search without list support returns [] — the
   *  page then shows history only, never a guessed price. */
  private fetchLive(regs: string[]): Observable<Map<string, NewReleaseResult>> {
    const uniq = Array.from(new Set(regs.map((r) => r.replace(/\s+/g, ''))));
    const chunks: string[][] = [];
    for (let i = 0; i < uniq.length; i += 120) chunks.push(uniq.slice(i, i + 120));
    if (!chunks.length) return of(new Map());
    const base = `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}`;
    return forkJoin(chunks.map((c) =>
      this.http.get<NewReleaseResult[]>(`${base}?process=new-release&filters=${encodeURIComponent('list:' + c.join(','))}`)
        .pipe(catchError(() => of([] as NewReleaseResult[]))))).pipe(
      map((parts) => {
        const m = new Map<string, NewReleaseResult>();
        parts.forEach((rows) => (rows || []).forEach((r) => {
          const reg = (r.registration || '').replace(/\s+/g, '');
          const owner = (r as any).owner ?? (r as any).plate_owner ?? (r as any).plateOwner;
          if (reg) m.set(reg, { ...r, owner } as NewReleaseResult);
        }));
        return m;
      }));
  }

  /** Released series: a plate no longer on sale shows what happened to it. */
  private applyHistory(c: PlateCard, h: PlateHistory | undefined): void {
    if (!h) return;
    const month = (d: string) => {
      const [y, m] = d.split('-').map(Number);
      return new Date(y, (m || 1) - 1, 1).toLocaleString('en-GB', { month: 'short', year: 'numeric' });
    };
    if (h.sold) c.historyNote = `Sold ${h.sold.by === 'DVLA auction' ? 'at DVLA auction' : 'at ' + h.sold.by}, ${month(h.sold.date)}`;
    else if (h.dvlaListed) c.historyNote = `Released by DVLA, ${month(h.dvlaListed.date)}`;
    if (c.state === 'available' || c.state === 'resale') return;   // live listing wins
    if (h.sold) {
      c.state = 'sold';
      c.price = Math.round(h.sold.pence / 100);
    } else if (h.dvlaListed) {
      c.state = 'sold';
      c.price = null;
    }
  }

  /** Pre-release, once DVLA has published the catalogue: its price and status. The sale has not
   *  opened, so a plate going on sale stays 'prerelease' (Notify me) but shows its price. */
  private applyDvla(c: PlateCard, d: DvlaPreSale | undefined, saleDay: string): void {
    if (!d || c.state !== 'prerelease') return;
    const auctionOnly = c.watch === 'auction_only';
    c.watch = undefined;
    if (d.status === 'available') {
      c.price = d.pence != null ? Math.round(d.pence / 100) : null;
      c.historyNote = `DVLA price. On sale from 10am, ${saleDay}.`;
    } else if (d.status === 'auction') {
      c.state = 'auction';
      c.historyNote = 'DVLA is holding this plate back for a future auction.';
    } else {
      c.state = 'unavailable';
      c.historyNote = auctionOnly
        ? `Not offered at a fixed price. DVLA has only ever sold ${c.plate.substring(0, 2)} plates at auction.`
        : 'DVLA is not offering this plate in the release.';
    }
  }

  private fetchPerfect(year: string): Observable<NewReleaseResult[]> {
    // Hit the plato_search endpoint directly — bypasses SearchService's
    // UserCheckCallback wrapper which queues callbacks forever for some
    // logged-in users (see #35 for the diagnosis).
    //
    // Uses the CANONICAL plato-search placeholder so the platex-dev2
    // Amplify Mode 2 sed rewrite does not redirect this to Mode 2 — Mode 2
    // does not implement `process=new-release` and would return `[]`. See
    // http-constants.ts and environment.prod.ts for the split-URL mechanism
    // that keeps this URL pointing at the real plato-search.
    const url =
      `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}` +
      `?process=new-release&filters=new-${year}-release`;
    return this.http.get<NewReleaseResult[]>(url).pipe(
      catchError((err) => {
        // Surface failures to console so a real diagnosis is possible from a
        // DevTools session — silent catchError on this fetch is the reason we
        // shipped fixture mocks for so long without noticing.
        console.error('[new-release] fetchPerfect failed', { url, year, err });
        return of([] as NewReleaseResult[]);
      }),
    );
  }

  private fetchNames(year: string): Observable<NewReleaseResult[]> {
    // UK (gb) 5-letter names with the year inside — the Names section rule for
    // every series. (plato-search also accepts `{year}.all` for every language;
    // deliberately not used here.)
    return this.fetchNamesFilter(year);
  }

  private fetchNamesFilter(filter: string): Observable<NewReleaseResult[]> {
    // Canonical placeholder — see fetchPerfect comment for the Mode 2 reason.
    const url =
      `${HTTPCONSTANTS.plato_search_canonical_protocol}${HTTPCONSTANTS.plato_search_canonical_base}` +
      `?process=new-release-name&filters=${encodeURIComponent(filter)}`;
    return this.http.get<NewReleaseResult[]>(url).pipe(
      catchError((err) => {
        console.error('[new-release] fetchNames failed', { url, filter, err });
        return of([] as NewReleaseResult[]);
      }),
    );
  }

  /**
   * Client-side classifier on the wordbank `term` field. Buckets results into
   * the four PerfectSectionId values by space-presence + word lengths. Empty
   * input → `{ totalCount: 0, sections: [] }` (template renders empty state).
   */
  private classifyPerfectPlates(rows: NewReleaseResult[]): {
    totalCount: number;
    sections: PerfectPlatesSection[];
  } {
    // Key order = section order on the page (user, 2026-10-23): Standouts, the
    // natural 4+3 reading, then 3+4, 2+5, repeating letters, and almost-perfect
    // (a vowel left out) as the lower section.
    const buckets: Record<PerfectSectionId, PlateCard[]> = {
      our_picks: [],
      single_word: [],
      standout_names: [],
      standout_foreign: [],
      standout_vehicles: [],
      two_word_4_3: [],
      two_word_3_4: [],
      two_word_2_5: [],
      two_word_5_2: [],
      repeating: [],
      almost_perfect: [],
    };

    for (const row of (rows || [])) {
      // banned_excluded plates are surfaced in the dedicated Banned section;
      // they would dilute the perfect-plates grid and they aren't buyable.
      if (row.auction_state === 'banned_excluded') continue;
      // auction_reserved plates surface in the dedicated "Heading to auction"
      // section. They're not buyable today (DVLA holds them back for future
      // auction) — leaving them in the perfect-plates grid would have an
      // auction-reserved plate win the featured/marquee slot purely on
      // phrase_quality_score, contradicting the buy-page intent of that slot.
      if (row.auction_state === 'auction_reserved') continue;
      const id = (row as NewReleaseResult & { nr_section?: PerfectSectionId }).nr_section || this.classifyTerm(row.term);
      if (id) buckets[id].push(this.toPlateCard(row));
    }

    const sections: PerfectPlatesSection[] = (Object.keys(buckets) as PerfectSectionId[])
      .filter((id) => buckets[id].length > 0)
      .map((id) => ({
        id,
        title: PERFECT_SECTION_META[id].title,
        description: PERFECT_SECTION_META[id].description,
        totalCount: buckets[id].length,
        featured: buckets[id][0],
        cards: buckets[id],
      }));

    // Almost-perfect plates aren't perfect plates: kept out of the 'Perfect plates' count.
    const totalCount = sections.filter((s) => s.id !== 'almost_perfect').reduce((acc, s) => acc + s.totalCount, 0);
    return { totalCount, sections };
  }

  private classifyTerm(term: string | null | undefined): PerfectSectionId | null {
    if (!term) return null;
    const trimmed = term.trim().toUpperCase();
    const spaceCount = (trimmed.match(/\s/g) || []).length;

    if (spaceCount === 0) {
      return trimmed.length === 7 ? 'single_word' : null;
    }
    if (spaceCount !== 1) return null;

    const [a, b] = trimmed.split(/\s+/);
    if (a.length === 3 && b.length === 4) return 'two_word_3_4';
    if (a.length === 4 && b.length === 3) return 'two_word_4_3';
    if (a.length === 2 && b.length === 5) return 'two_word_2_5';
    if (a.length === 5 && b.length === 2) return 'two_word_5_2';
    return null;
  }

  private buildNamesSection(rows: NewReleaseResult[]): {
    totalCount: number;
    featured: PlateCard | null;
    cards: PlateCard[];
    azCounts: Record<string, number>;
  } {
    // Same banned_excluded + auction_reserved filter as perfectPlates —
    // those plates appear in dedicated sections (Banned / Heading to
    // auction) not the names grid.
    const cards = (rows || [])
      .filter((r) =>
        r.auction_state !== 'banned_excluded' &&
        r.auction_state !== 'auction_reserved')
      .map((r) => this.toPlateCard(r));
    const azCounts: Record<string, number> = {};
    for (const card of cards) {
      const first = (card.word || '').charAt(0).toUpperCase();
      if (first) azCounts[first] = (azCounts[first] || 0) + 1;
    }
    return {
      totalCount: cards.length,
      featured: cards.length > 0 ? cards[0] : null,
      cards,
      azCounts,
    };
  }

  /**
   * Build the "Heading to auction" section from the perfect-plates and
   * names rows. Pulls every row tagged `auction_state === 'auction_reserved'`
   * from both sets, dedupes by registration, then splits the result into
   * two sub-sections:
   *
   *   - marqueePicks: phrase_quality_score > 0 — LLM-ranked editorial
   *     picks. Sorted by score DESC (word_score ASC as tiebreak). For
   *     Series 76 today: GO76 AME (9) + NO76 OOD (4). The template gives
   *     the first entry the marquee framing.
   *   - namesAuction: phrase_quality_score === 0 — DVLA-reserved name
   *     plates that didn't run through phrase scoring. Quality 0 here
   *     means "not scored", not "low quality" — these are real
   *     auction-reserved inventory (GA76 YLE / BO76 BBY / PE76 NNY etc.)
   *     and they earn their place on the page. Sorted alphabetically by
   *     `word` so users can scan the name catalogue.
   *
   * Populates from rows already returned by fetchPerfect / fetchNames —
   * no additional HTTP. Empty arrays for series whose held_back_plates
   * classifier hasn't run yet (everything except 76 today); the
   * template's *ngIf hides the section.
   */
  private buildHeadingToAuction(
    perfect: NewReleaseResult[],
    names: NewReleaseResult[],
  ): NewReleaseHeadingToAuction {
    const seen = new Set<string>();
    const all = [...(perfect || []), ...(names || [])]
      .filter((r) => r.auction_state === 'auction_reserved')
      .filter((r) => {
        const reg = (r.registration || '').replace(/\s+/g, '');
        if (!reg || seen.has(reg)) return false;
        seen.add(reg);
        return true;
      });

    const marqueePicks: HeadingToAuctionEntry[] = all
      .filter((r) => (r.phrase_quality_score ?? 0) > 0)
      .sort((a, b) => {
        const qa = a.phrase_quality_score ?? 0;
        const qb = b.phrase_quality_score ?? 0;
        if (qb !== qa) return qb - qa;
        return (a.word_score ?? 999999) - (b.word_score ?? 999999);
      })
      .map((r) => ({
        plate: this.formatPlate(r),
        word: (r.term || '').toUpperCase(),
        phraseQualityScore: r.phrase_quality_score ?? 0,
      }));

    const namesAuction: HeadingToAuctionEntry[] = all
      .filter((r) => (r.phrase_quality_score ?? 0) === 0)
      .map((r) => ({
        plate: this.formatPlate(r),
        word: (r.term || '').toUpperCase(),
        phraseQualityScore: 0,
      }))
      .sort((a, b) => a.word.localeCompare(b.word));

    return { marqueePicks, namesAuction };
  }

  /**
   * Map NewReleaseResult to PlateCard. The Lambda price import (2026-05-14)
   * means `price` is pence-denominated (e.g. 39900 = £399) for plates that
   * have been priced; `price === -1` is the sentinel for wordbank rows that
   * haven't been priced (typically auction_state === 'auction_reserved'
   * OR a backend pricing gap).
   *
   * State derivation now consumes the new `auction_state` field:
   *   - auction_state = 'auction_reserved' → 'auction' (renders "Coming to auction")
   *   - owner = -1                         → 'sold' (DVLA-held)
   *   - owner > 0                          → 'resale' (user-held)
   *   - price > 0, owner = 0               → 'available'
   *   - everything else                    → 'unavailable' (owner -2 / no
   *                                          price; legacy shows Notify only)
   *
   * `banned_excluded` rows are filtered before reaching this function — they
   * render exclusively in the Banned section.
   */
  private toPlateCard(r: NewReleaseResult): PlateCard {
    const state = this.deriveState(r);
    const pricePounds = r.price && r.price > 0 ? Math.round(r.price / 100) : null;
    return {
      plate: this.formatPlate(r),
      word: r.term || '',
      state,
      price: state === 'auction' || state === 'unavailable' ? null : pricePounds,
      newIssue: state === 'available' && pricePounds != null,
      qualityScore: r.phrase_quality_score != null ? r.phrase_quality_score : 0,
      languages: r.languages && r.languages.length ? r.languages : undefined,
    };
  }

  /** Rows built by getPreReleasePage carry the series in their registration. */
  private preReleaseRow(r: NewReleaseResult): boolean {
    const reg = (r.registration || '').replace(/\s+/g, '');
    return reg.length === 7 && isPreRelease(reg.substring(2, 4));
  }

  private deriveState(r: NewReleaseResult): PlateState {
    if (r.auction_state === 'auction_reserved') return 'auction';
    if (r.owner === -2 && r.price === -1 && !r.auction_state && this.preReleaseRow(r)) return 'prerelease';
    if (r.owner === -1) return 'sold';
    if (r.owner != null && r.owner > 0) return 'resale';
    if (typeof r.price === 'number' && r.price > 0 && r.owner === 0) return 'available';
    // Sentinel `price = -1, owner = -2` with no auction_state: no longer
    // buyable (or not yet priced). Only DVLA's explicit auction_reserved flag
    // means "coming to auction" — labelling these as auction mislabelled e.g.
    // 82 of the 25-series perfect plates. Card shows "Not available" + Notify.
    return 'unavailable';
  }

  /**
   * Canonical plate formatter — defers to RegistrationService which is the
   * site-wide source of truth (handles current, prefix, suffix and dateless
   * styles). The wordbank/byo endpoints DO populate `formattedRegistration`
   * but we re-format defensively so a stale or empty value can't leak an
   * unspaced plate into the UI.
   */
  private formatPlate(r: NewReleaseResult | { registration?: string; formattedRegistration?: string }): string {
    const raw = (r.registration || r.formattedRegistration || '').replace(/\s+/g, '');
    if (!raw) return '';
    return this.registrationService.LocalFormatRegistration(raw);
  }

  /**
   * Real sold plates for the ticker. Single source: the MarketWatch
   * `/recently-sold-by-series?series={year}` endpoint that runs the
   * Stage-2-verified series-scoped query against the prod platex-pricing
   * cluster (registration regex `^[A-Z][A-Z]{year}[A-Z][A-Z][A-Z]$`,
   * sale_type IN (0,1,6), price > 8000p, sold_date >= launch date,
   * ORDER BY sold_date DESC LIMIT 30 by default).
   *
   * Hugh's rule, encoded here: only REAL sold plates show in the rail —
   * never featured / available listings, never picks, never fixtures.
   * When the endpoint returns `[]` we emit `[]` and the hero's *ngIf
   * hides the ticker entirely. No client-side dedupe, no client-side
   * filter, no second source — the backend is the single source of truth
   * and the response shape already encodes "only real sales for this
   * series."
   */
  getRecentlySoldForYear(year: string): Observable<TickerEntry[]> {
    const url =
      `${HTTPCONSTANTS.dataApiProtocol}${HTTPCONSTANTS.dataApiBaseAddress}` +
      `/MarketWatch/recently-sold-by-series?series=${encodeURIComponent(year)}`;
    return this.http.get<RecentlySoldRow[]>(url).pipe(
      map((rows) => (rows || [])
        .slice(0, TICKER_MAX_ENTRIES)
        .map((r) => ({
          plate: this.formatPlate(r),
          // soldPrice is in pence (matches the homepage /recently-sold contract).
          price: Math.round((r.soldPrice || 0) / 100),
        }))),
      catchError(() => of([] as TickerEntry[])),
    );
  }

  /**
   * The first (chronologically earliest) sold plates of the series — the
   * launch-day order book — for the "first 100 plates buyers bought"
   * section. Single source: `/MarketWatch/first-sold-by-series?series=`,
   * which runs `ORDER BY sold_date ASC LIMIT 100`.
   *
   * Computes `rushDuration` — the span between the first and last entry —
   * but only claims it when the data genuinely describes a launch rush:
   *   - at least `FIRST_SOLD_FULL_SET` (100) entries, AND
   *   - the span is under 24h.
   * Otherwise `rushDuration` is null and the template drops the
   * "— sold in … from launch" clause. This keeps the header honest for
   * sparse series (< 100 sales) and for older series whose first-100
   * spans days rather than launch-morning minutes.
   */
  getFirstSoldForYear(year: string): Observable<NewReleaseFirstSold> {
    const url =
      `${HTTPCONSTANTS.dataApiProtocol}${HTTPCONSTANTS.dataApiBaseAddress}` +
      `/MarketWatch/first-sold-by-series?series=${encodeURIComponent(year)}`;
    return this.http.get<RecentlySoldRow[]>(url).pipe(
      map((rows) => {
        const entries: FirstSoldEntry[] = (rows || []).map((r) => ({
          plate: this.formatPlate(r),
          price: Math.round((r.soldPrice || 0) / 100),
          soldDate: r.soldDate,
        }));
        return {
          entries,
          rushDuration: this.computeRushDuration(entries),
        };
      }),
      catchError(() => of({ entries: [], rushDuration: null } as NewReleaseFirstSold)),
    );
  }

  /** First sold for a released series, only where DVLA's online sale gives the purchase
   *  order and times (72 series onwards, May 2022). Earlier series only have DVLA auction
   *  rows dated to the month, which say nothing about who bought first, so they get null
   *  and the section stays hidden. */
  private timedFirstSold(fs: NewReleaseFirstSold): NewReleaseFirstSold | null {
    const timed = fs.entries.filter((e) => !/T00:00:00/.test(e.soldDate || '')).length;
    return fs.entries.length && timed >= fs.entries.length * 0.9 ? fs : null;
  }

  /**
   * Human-readable span between the first and last entry, or null when the
   * data shouldn't claim a "launch rush" — see getFirstSoldForYear.
   */
  private computeRushDuration(entries: FirstSoldEntry[]): string | null {
    if (entries.length < FIRST_SOLD_FULL_SET) return null;
    const first = Date.parse(entries[0].soldDate);
    const last = Date.parse(entries[entries.length - 1].soldDate);
    if (isNaN(first) || isNaN(last) || last < first) return null;
    const totalSec = Math.round((last - first) / 1000);
    if (totalSec >= 86400) return null; // spans a day or more — not a rush
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const unit = (n: number, w: string) => `${n} ${w}${n === 1 ? '' : 's'}`;
    if (h > 0) return `${unit(h, 'hour')} ${unit(m, 'minute')}`;
    if (m > 0) return `${unit(m, 'minute')} ${unit(s, 'second')}`;
    return unit(s, 'second');
  }
}
